import { Button } from '@/components/ui/button';
import { SlidersHorizontal, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useI18n } from '@/providers/i18n';
import { AdvancedOptionsPanel } from './advanced-options-panel';

type AdvancedToggleButtonProps = {
  /** 当前是否展开（已结合 disabled 计算的有效态） */
  expanded: boolean;
  disabled?: boolean;
  onToggle: () => void;
};

// 工具栏内的「高级选项」切换按钮：点击展开/收起内嵌面板。
// 抽成公共组件，供 image / video / audio 等节点复用，避免重复样式与行为。
export function AdvancedToggleButton({ expanded, disabled, onToggle }: AdvancedToggleButtonProps) {
  const { t } = useI18n();
  return (
    <Button
      size="sm"
      variant="ghost"
      className={cn('rounded-md h-8 px-2 text-xs', expanded && 'bg-accent text-accent-foreground')}
      disabled={disabled}
      onClick={onToggle}
      aria-expanded={expanded}
    >
      <SlidersHorizontal size={12} className="mr-1" />
      {t('Advanced options')}
      <ChevronDown size={12} className={cn('ml-1 transition-transform', expanded && 'rotate-180')} />
    </Button>
  );
}

type AdvancedInlinePanelProps = {
  id: string;
  uiOptions: any[];
  modelUi: Record<string, unknown> | undefined;
  canUseAdvanced: boolean;
  onChange: (key: string, value: unknown) => void;
  /** 每行卡片数量，默认 3 */
  columns?: number;
  /** 面板最大高度（px），超出滚动，默认 280 */
  maxHeightPx?: number;
};

// 内嵌于工具栏底部的高级选项面板（带最大高度滚动）。
export function AdvancedInlinePanel({
  id,
  uiOptions,
  modelUi,
  canUseAdvanced,
  onChange,
  columns = 3,
  maxHeightPx = 280,
}: AdvancedInlinePanelProps) {
  return (
    <div
      className="overflow-y-auto overflow-x-hidden pr-1 advanced-options-scrollbar"
      style={{ maxHeight: `${maxHeightPx}px` }}
    >
      <AdvancedOptionsPanel
        id={id}
        uiOptions={uiOptions}
        modelUi={modelUi}
        canUseAdvanced={canUseAdvanced}
        columns={columns}
        onChange={onChange}
      />
    </div>
  );
}
